import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { apiFetch, v1Path } from '../../lib/api';
import { memberDisplayName, type MemberDto } from '../../lib/work-types';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

export const BLOCKED_REASON_MAX = 2000;
export const BLOCKED_BY_MAX = 20;

export type BlockedSubmit = { reason: string; blockedByIds: string[] };

/** Everyone an item is waiting on, falling back to the single field older rows carry. */
export function blockedByIdsOf(item: { blocked_by_ids?: string[] | null; blocked_by_id?: string | null }): string[] {
  if (item.blocked_by_ids && item.blocked_by_ids.length > 0) return item.blocked_by_ids;
  return item.blocked_by_id ? [item.blocked_by_id] : [];
}

/** The named blockers as "Ann, Bob and Cy", skipping ids no longer in the member list. */
export function blockedByNames(ids: string[], members: MemberDto[] | undefined): string {
  const names = ids
    .map((id) => members?.find((member) => member.user_id === id))
    .filter((member): member is MemberDto => Boolean(member))
    .map((member) => memberDisplayName(member));
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export default function BlockedReasonDialog({
  open,
  onOpenChange,
  workItemKey,
  projectId,
  members,
  initialReason,
  initialBlockedByIds,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workItemKey: string;
  /** Used to load the people list when `members` is not passed. */
  projectId?: string;
  members?: MemberDto[];
  initialReason?: string | null;
  initialBlockedByIds?: string[];
  onSubmit: (next: BlockedSubmit) => Promise<unknown>;
}) {
  const [reason, setReason] = useState(initialReason ?? '');
  const [blockedByIds, setBlockedByIds] = useState<string[]>(initialBlockedByIds ?? []);
  const [query, setQuery] = useState('');
  const [loadedMembers, setLoadedMembers] = useState<MemberDto[] | null>(null);
  const [saving, setSaving] = useState(false);
  const editing = Boolean(initialReason);
  const initialKey = (initialBlockedByIds ?? []).join(',');

  const people = useMemo(() => {
    const active = (members ?? loadedMembers ?? []).filter((member) => member.is_active !== false);
    return active.sort((left, right) => memberDisplayName(left).localeCompare(memberDisplayName(right)));
  }, [members, loadedMembers]);
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return people;
    return people.filter((member) => `${memberDisplayName(member)} ${member.email ?? ''}`.toLowerCase().includes(needle));
  }, [people, query]);
  const atMax = blockedByIds.length >= BLOCKED_BY_MAX;
  const selectedNames = blockedByNames(blockedByIds, people);

  useEffect(() => {
    if (!open) return;
    setReason(initialReason ?? '');
    setBlockedByIds(initialKey ? initialKey.split(',') : []);
    setQuery('');
  }, [open, initialReason, initialKey]);

  useEffect(() => {
    if (!open || members || loadedMembers || !projectId) return;
    let cancelled = false;
    void apiFetch<{ items?: MemberDto[] }>(v1Path(`/projects/${projectId}/members`))
      .then((res) => {
        if (!cancelled && Array.isArray(res.items)) setLoadedMembers(res.items);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, members, loadedMembers, projectId]);

  function toggle(userId: string, checked: boolean) {
    setBlockedByIds((current) =>
      checked ? (current.includes(userId) ? current : [...current, userId]) : current.filter((id) => id !== userId),
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const trimmed = reason.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await onSubmit({ reason: trimmed, blockedByIds });
      onOpenChange(false);
    } catch {
      // The caller has already shown the error; keep the dialog open so the text is not lost.
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit why ${workItemKey} is blocked` : `Mark ${workItemKey} as blocked`}</DialogTitle>
            <DialogDescription>
              Say what it's waiting on, so people know how to unblock it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="blocked-reason">Why is it blocked?</Label>
            <Textarea
              id="blocked-reason"
              autoFocus
              rows={4}
              maxLength={BLOCKED_REASON_MAX}
              value={reason}
              placeholder="e.g. Waiting on the signed contract from FameCake"
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium leading-none">Who is it waiting on? (optional)</legend>
            <p className="text-xs text-muted-foreground" aria-live="polite">
              {blockedByIds.length === 0 ? 'Nobody in particular' : selectedNames}
            </p>
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search people"
              aria-label="Search people"
            />
            <ul className="max-h-48 space-y-0.5 overflow-y-auto rounded-md border p-1">
              {visible.length === 0 ? (
                <li className="px-2 py-1.5 text-sm text-muted-foreground">No one matches.</li>
              ) : (
                visible.map((member) => {
                  const checked = blockedByIds.includes(member.user_id);
                  const id = `blocked-by-${member.user_id}`;
                  return (
                    <li key={member.user_id} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-muted/50">
                      <Checkbox
                        id={id}
                        checked={checked}
                        disabled={!checked && atMax}
                        onCheckedChange={(next) => toggle(member.user_id, next === true)}
                      />
                      <Label htmlFor={id} className="flex-1 cursor-pointer font-normal">
                        {memberDisplayName(member)}
                      </Label>
                    </li>
                  );
                })
              )}
            </ul>
            {atMax ? <p className="text-xs text-muted-foreground">You can name up to {BLOCKED_BY_MAX} people.</p> : null}
          </fieldset>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={saving || !reason.trim()}>
              {editing ? 'Save' : 'Mark blocked'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
